import { VEHICLES, getVehiclePrice } from './bookingConfig';
import { FEATURED_ROUTE_PROMOTIONS } from './featuredRoutes';
import { HUB_LANDING_PAGES } from './hubLanding';
import { displayPlaceName, ROUTE_LANDING_PAGES } from './routeLanding';
import { LOCAL_CORRIDOR_PAGES } from './localCorridorPages';
import { quoteOneWayPackage } from './localCorridorFares';

const distanceByPath = new Map(
  ROUTE_LANDING_PAGES.map((page) => [page.slug, page.distanceKm || null]),
);

const tariffByPath = new Map(
  LOCAL_CORRIDOR_PAGES.map((page) => [page.slug, page.tariff || null]),
);

const featuredPromo = FEATURED_ROUTE_PROMOTIONS[0];

/** Fixed one-way totals for both Coimbatore ↔ Ooty directions. */
export const OOTY_ONE_WAY_FARES = {
  Sedan: 2699,
  SUV: 3500,
  MUV: 4300,
};

const OOTY_FIXED_PATHS = new Set(['/coimbatore-to-ooty', '/ooty-to-coimbatore']);

const placeKey = (value) => String(value || '').trim().toLowerCase();

export const coimbatoreOotyOneWayFare = (origin, destination, vehicleType) => {
  const left = placeKey(origin);
  const right = placeKey(destination);
  const isPair = (left === 'coimbatore' && right === 'ooty')
    || (left === 'ooty' && right === 'coimbatore');
  if (!isPair) return null;
  const amount = OOTY_ONE_WAY_FARES[vehicleType];
  return amount == null ? null : amount;
};

const samePlace = (a, b) => String(a || '').trim().toLowerCase() === String(b || '').trim().toLowerCase();

/**
 * Tolls and parking are included only for Coimbatore ↔ Ooty,
 * and for corridor packages that already list both as included extras.
 */
export const tollsAndParkingIncluded = (origin, destination, path) => {
  const places = [origin, destination].map((name) => String(name || '').trim().toLowerCase());
  if (places.includes('coimbatore') && places.includes('ooty')) return true;
  const extras = tariffByPath.get(path)?.includedExtras || [];
  return extras.includes('toll') && extras.includes('parking');
};

/**
 * Same totals as the booking form.
 * Package routes use the published one-way package. Every other route uses
 * the live mapped kilometres with getVehiclePrice. Catalogue kilometres are
 * not a fare distance.
 * Returns null when that vehicle is unpublished on a package, or when a
 * non-package route does not yet have a live distance.
 */
export const quoteRouteVehicleFare = (path, vehicleType, distanceKm) => {
  if (OOTY_FIXED_PATHS.has(path)) {
    const fixed = OOTY_ONE_WAY_FARES[vehicleType];
    if (fixed != null) return fixed;
  }

  const tariff = tariffByPath.get(path);
  if (tariff?.oneWayPackage) {
    const quote = quoteOneWayPackage(
      tariff,
      vehicleType,
      distanceKm || tariff.includedDistanceKm,
    );
    return quote ? quote.total : null;
  }

  const km = Number(distanceKm);
  if (!Number.isFinite(km) || km <= 0) return null;
  const vehicle = VEHICLES.find((item) => item.type === vehicleType);
  if (!vehicle) return null;
  return getVehiclePrice(km, vehicle.rate, 'One Way');
};

export const catalogueDistanceKm = (path) => {
  const km = distanceByPath.get(path);
  return Number(km) || null;
};

const pushRoute = (list, seen, route) => {
  if (!route?.path || seen.has(route.path)) return;
  seen.add(route.path);
  list.push({
    id: route.path,
    path: route.path,
    origin: displayPlaceName(route.origin),
    destination: displayPlaceName(route.destination),
    tripTypeLabel: 'One Way Taxi',
    catalogueKm: catalogueDistanceKm(route.path),
  });
};

const collectKnownRoutes = () => {
  const list = [];
  const seen = new Set();

  (featuredPromo?.legs || []).forEach((leg) => {
    pushRoute(list, seen, {
      path: leg.detailsPath,
      origin: leg.origin,
      destination: leg.destination,
    });
  });

  HUB_LANDING_PAGES.forEach((hub) => {
    (hub.priorityRoutes || []).forEach((route) => {
      pushRoute(list, seen, {
        path: route.path,
        origin: route.origin,
        destination: route.destination,
      });
      pushRoute(list, seen, {
        path: route.reversePath,
        origin: route.destination,
        destination: route.origin,
      });
    });
  });

  return list;
};

/**
 * Cards come from featured legs and hub priority routes that already have URLs.
 * The focused pair is placed first. Homepage (no focus) leads with Coimbatore ↔ Ooty.
 */
export const getPopularRouteSlides = (focus = {}) => {
  const origin = focus.origin || featuredPromo?.legs?.[0]?.origin || 'Coimbatore';
  const destination = focus.destination || featuredPromo?.legs?.[0]?.destination || 'Ooty';
  const all = collectKnownRoutes();

  const focusPath = all.find((route) => (
    samePlace(route.origin, origin) && samePlace(route.destination, destination)
  ))?.path;

  if (!focusPath && origin && destination) {
    const slug = `/${String(origin).trim().toLowerCase().replace(/[^a-z0-9]+/g, '-')}-to-${String(destination).trim().toLowerCase().replace(/[^a-z0-9]+/g, '-')}`;
    const known = [
      ...ROUTE_LANDING_PAGES.map((page) => page.slug),
      ...(featuredPromo?.legs || []).map((leg) => leg.detailsPath),
    ];
    const path = known.find((item) => item === slug || item === `${slug}-taxi`) || null;
    if (path) {
      all.unshift({
        id: path,
        path,
        origin: displayPlaceName(origin),
        destination: displayPlaceName(destination),
        tripTypeLabel: 'One Way Taxi',
        catalogueKm: catalogueDistanceKm(path),
      });
    }
  }

  const related = all.filter((route) => (
    samePlace(route.origin, origin)
    || samePlace(route.destination, origin)
    || samePlace(route.origin, destination)
    || samePlace(route.destination, destination)
  ));
  const pool = related.length ? related : all.filter((route) => samePlace(route.origin, 'Coimbatore'));
  const slides = (pool.length ? pool : all).slice();

  slides.sort((a, b) => {
    const rank = (route) => {
      if (samePlace(route.origin, origin) && samePlace(route.destination, destination)) return 0;
      if (samePlace(route.origin, destination) && samePlace(route.destination, origin)) return 1;
      if (samePlace(route.origin, origin)) return 2;
      return 3;
    };
    return rank(a) - rank(b);
  });

  return {
    badge: 'Popular Routes',
    heading: `${displayPlaceName(origin)} ↔ ${displayPlaceName(destination)} Taxi`,
    supportingText: featuredPromo?.supportingText || 'One Way Taxi • Round Trip • Airport Transfer',
    slides,
  };
};
